<div class="container mx-auto" id="external-contract-overview-component">
  <!-- Section for displaying the title -->
  <div class="mt-8">
    <h1 class="font-['Apex_Rounded_Medium'] text-3xl">{{ "menu.externalContracts" | translate }}</h1>
    
    <!-- Grid layout for additional contract details -->
    <div class="grid xl:grid-cols-8 sm:grid-cols-6 grid-cols-4 text-sm mt-3">
      <!-- Location Information -->
      <div class="col-span-2 md:mb-0 mb-3">
        <!-- Label and value for location information -->
        <p class="text-primary">{{ "contract.location" | translate }}</p>
        <p>{{ location() ?? '-' }}</p>
      </div>

      <!-- Personal Customer Advisor Information -->
      <div class="col-span-2 md:mb-0 mb-3">
        <!-- Label and value for personal customer advisor -->
        <p class="text-primary">{{ "contract.personalCustomerAdvisor" | translate }}</p>
        <p>{{ advisor()?.name ?? '-' }}</p>
      </div>
    </div>
  </div>

  <div class="mt-8">
    <p-panel [showHeader]="false">
      <div id="external-contract-table-component" class="py-1.5 external-contract-table">
        <p-table #contractTable [value]="externalContracts()" [loading]="isLoading()" [paginator]="true" [rows]="10"
          [rowsPerPageOptions]="[10, 25, 50]"
          [currentPageReportTemplate]="'primeng.currentPageReportTemplate' | translate" [showCurrentPageReport]="true"
          dataKey="contractNumber" [resizableColumns]="true" columnResizeMode="expand" [scrollable]="true" class="md:text-base text-sm"
          [showLoader]="false"
          sortField="status"
          [sortOrder]="1"
          [globalFilterFields]="['contractName', 'contractNumber', 'orderNumber']"
          [gateTableStateKey]="tableStateKey">

          <!-- Table Caption -->
          <ng-template pTemplate="caption">
            <div class="grid md:grid-cols-12 md:text-base text-sm">
              <div class="col-span-3 my-auto md:mx-0 mb-3">
                <p class="font-['Apex_Rounded_Medium'] text-sm">{{ "menu.externalContracts" | translate }}</p>
              </div>
              <div class="md:col-span-9 sm:text-right text-center">
                <div class="md:flex grid grid-cols-12 gap-2 justify-end gap-4">
                  <div class="col-span-12 md:col-span-6">
                    <p-button [label]="'clearFilter' | translate" severity="danger" 
                      icon="pi pi-filter-slash" (click)="clearAllFilter(contractTable)"
                      styleClass="md:w-auto w-full md:text-base text-sm"></p-button>
                  </div>
                  
                  <div class="col-span-12 md:col-span-6 flex">
                    <p-iconfield iconPosition="left" class="w-full">
                      <p-inputicon>
                        <i class="pi pi-search"></i>
                      </p-inputicon>
                      <input pInputText type="text" [ngModel]="searchValue()" (ngModelChange)="onGlobalSearch($event, contractTable)"
                        [placeholder]="'searchPlaceholder' | translate" class="md:text-base text-sm w-full" />
                    </p-iconfield>
                  </div>
                </div>
              </div>
            </div>
          </ng-template>

          <!-- Table Header -->
          <ng-template pTemplate="header">
            <tr>
              <th pResizableColumn pSortableColumn="contractName" style="width: 20%">
                {{ 'contract.contractName' | translate }}
                <p-sortIcon field="contractName"></p-sortIcon>
              </th>
              <th pResizableColumn pSortableColumn="contractNumber" style="width: 20%">
                {{ 'contract.contractNumber' | translate }}
                <p-sortIcon field="contractNumber"></p-sortIcon>
              </th>
              
              <th pResizableColumn pSortableColumn="description" style="width: 20%">
                {{ 'contract.description' | translate }}
                <p-sortIcon field="description"></p-sortIcon>
              </th>
              <th pResizableColumn pSortableColumn="startDate">
                {{ 'contract.startDate' | translate }}
                <p-sortIcon field="startDate"></p-sortIcon>
              </th>
              <th pResizableColumn pSortableColumn="endDate">
                {{ 'contract.endDate' | translate }}
                <p-sortIcon field="endDate"></p-sortIcon>
              </th>
              <th pResizableColumn pSortableColumn="orderNumber">
                {{ 'contract.orderNumber' | translate }}
                <p-sortIcon field="orderNumber"></p-sortIcon>
              </th>
              <th pResizableColumn pSortableColumn="status">
                {{ 'contract.status' | translate }}
                <p-sortIcon field="status"></p-sortIcon>
                <p-columnFilter field="status" matchMode="equals" display="menu" [showOperator]="false"
                  [showMatchModes]="false" [showAddButton]="false" [hideOnClear]="true" [showApplyButton]="false"
                  #statusFilter>
                  <ng-template #filtericon let-hasFilter="hasFilter">
                    <gate-table-filter-icon
                      [hasFilter]="hasFilter"
                      [table]="contractTable"
                      field="status"
                      [options]="contractStatuses" />
                  </ng-template>
                  <ng-template pTemplate="filter" let-value let-filter="filterCallback">
                    <p-select [options]="contractStatuses" optionLabel="key" [ngModel]="value"
                      (onChange)="filter($event.value); statusFilter.hide()" optionValue="value"
                      [placeholder]="'selectStatus' | translate">
                    </p-select>
                  </ng-template>
                </p-columnFilter>
              </th>
            </tr>
          </ng-template>

          <!-- Table Body -->
          <ng-template pTemplate="body" let-contract>
            <tr>
              <td>
                <span class="p-column-title">{{ 'contract.contractName' | translate }}</span>
                <div class="line-clamp-2 whitespace-normal break-words" [title]="contract.contractName">{{ contract.contractName }}</div>
              </td>
              <td>
                <span class="p-column-title">{{ 'contract.contractNumber' | translate }}</span>
                <div class="line-clamp-2 whitespace-normal break-words" [title]="contract.contractNumber">{{ contract.contractNumber }}</div>
              </td>
              <td>
                <span class="p-column-title">{{ 'contract.description' | translate }}</span>
                <div class="line-clamp-2 whitespace-normal break-words" [title]="contract.description">{{ contract.description }}</div>
              </td>
              <td>
                <span class="p-column-title">{{ 'contract.startDate' | translate }}</span>
                <span>{{ contract.startDate | date: 'dd.MM.yyyy' }}</span>
              </td>
              <td>
                <span class="p-column-title">{{ 'contract.endDate' | translate }}</span>
                <span [ngClass]="{ 'font-bold': isEndingWithin3Months(contract.endDate) }">{{ contract.endDate | date: 'dd.MM.yyyy' }}</span>
              </td>
              <td>
                <span class="p-column-title">{{ 'contract.orderNumber' | translate }}</span>
                <div class="flex flex-wrap gap-1">
                  @for (orderNo of parseOrderNumbers(contract.orderNumber); track orderNo; let last = $last) {
                    <a [routerLink]="['/order/', orderNo.replaceAll('/', '_')]" target="_blank" class="underline cursor-pointer">
                      {{ orderNo }}
                    </a>@if (!last) {<span>,</span>}
                  }
                  @if (!contract.orderNumber) {
                    <span>-</span>
                  }
                </div>
              </td>
              <td>
                <span class="p-column-title">{{ 'contract.status' | translate }}</span>
                <span>
                  <p-tag [value]="contract.status | ContractStatus | translate" [severity]="getSeverity(contract.status)"
                    [ngStyle]="{ 'left.px': 5, 'top.px': 5 }" />
                </span>
              </td>
            </tr>
          </ng-template>

          <!-- SKELETON BODY ROWS -->
          <ng-template pTemplate="loadingbody">
            @for (i of [1,2,3,4,5]; track i) {
            <tr class="skeleton-row">
              <td class="block md:table-cell py-2"><p-skeleton class="md:w-[50%] w-full" height="1rem"></p-skeleton></td>
              <td class="block md:table-cell py-2"><p-skeleton class="md:w-[65%] w-full" height="1rem"></p-skeleton></td>
              <td class="block md:table-cell py-2"><p-skeleton class="md:w-[70%] w-full" height="1rem"></p-skeleton></td>
              <td class="block md:table-cell py-2"><p-skeleton class="md:w-[60%] w-full" height="1rem"></p-skeleton></td>
              <td class="block md:table-cell py-2"><p-skeleton class="md:w-[60%] w-full" height="1rem"></p-skeleton></td>
              <td class="block md:table-cell py-2"><p-skeleton class="md:w-[65%] w-full" height="1rem"></p-skeleton></td>
              <td class="block md:table-cell py-2"><p-skeleton class="md:w-[55%] w-full" height="1rem"></p-skeleton></td>
            </tr>
            }
          </ng-template>
          
          <ng-template #emptymessage>
            <tr class="emptymessage">
              <td colspan="7">
                <div class="text-center">
                  <i class="pi pi-exclamation-triangle text-yellow-500" style="font-size: 2rem"></i>
                  <p>{{ "contract.noRecordsFound" | translate }}</p>
                </div>
              </td>
            </tr>
          </ng-template>
        </p-table>
      </div>
    </p-panel>
  </div>
</div>
